// Student AttendancePage — own attendance history.
import { useState } from "react";
import { useAuth } from "../../hooks/useAuth.js";
import { useFetch } from "../../hooks/useApi.js";
import Badge from "../../components/shared/Badge.js";
import type { AttendanceRecord, PaginatedResult } from "@dm-lms/shared/types";

export default function AttendancePage() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useFetch<PaginatedResult<AttendanceRecord>>(
    user ? `/api/attendance/student/${user.id}?page=${page}&pageSize=20` : null
  );

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Attendance</h1>

      {isLoading && <p className="text-dm-slate text-sm">Loading attendance…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!isLoading && !error && data && (
        <>
          {data.items.length === 0 ? (
            <p className="text-dm-slate text-sm">No attendance records yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-dm-slate">
                  <tr>
                    <th className="px-4 py-3 font-medium">Session ID</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Checked in</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.items.map((record) => (
                    <tr key={record.id}>
                      <td className="px-4 py-3">{record.sessionId}</td>
                      <td className="px-4 py-3">
                        <Badge tone={record.status === "present" ? "green" : record.status === "excused" ? "gold" : "red"}>
                          {record.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {record.checkedInAt ? new Date(record.checkedInAt).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4">
            <button
              type="button"
              className="text-sm px-3 py-1.5 rounded-md border border-gray-300 disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="text-xs text-dm-slate">Page {data.page} · {data.total} total</span>
            <button
              type="button"
              className="text-sm px-3 py-1.5 rounded-md border border-gray-300 disabled:opacity-40"
              disabled={!data.hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
